import 'dayjs/locale/es'
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import type { CSSProperties } from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { DailyCarrierMetric, DailyMetricsResponse, DailySupplierMetric } from '@api'
import { useDashboardFilters } from '@contexts'

import { matchesFillFilter, type DailyMetricFillFilter } from '../dailyMetricFillFilter'
import { DailyMetricFillSelector } from './DailyMetricFillSelector'
import { DaySelector } from './DaySelector'
import { MetricsCenteredCell, TruncatedCarrierHeader } from './metricsTableCells'
import {
  DailyMetricsTableWrap,
  DailyTableDateBar,
  DailyTableToolbar,
  DailyTableToolbarCenter,
  DailyTableToolbarFilters,
} from './styled'

dayjs.locale('es')

function breachHeatBackground(value: number, orders: number): CSSProperties {
  if (value <= 0 || orders <= 0) {
    return { fontWeight: 600 }
  }
  const t = Math.min(1, value / orders)
  if (t <= 0.25) {
    return { fontWeight: 600 }
  }
  const g = t > 0.75 ? 150 : t > 0.5 ? 195 : t > 0.25 ? 226 : 255

  return {
    background: `rgb(255, ${g}, ${g})`,
    fontWeight: 600,
  }
}

interface TableRow {
  key: string
  supplierLabel: string
  orders: number
  breaches: number
  /** Confirmed orders per carrier column (same order as carrierColumns) */
  carrierSlots: number[]
}

/** Totals row — rendered in Table.Summary with fixed="bottom", not in scroll body */
interface DailySummaryTotals {
  supplierLabel: string
  orders: number
  breaches: number
  carrierSlots: number[]
}

function buildCarrierColumns(
  suppliers: DailySupplierMetric[],
): { carrierId: number; tradeName: string }[] {
  const meta = new Map<number, string>()
  for (const s of suppliers) {
    for (const c of s.carriers ?? []) {
      if (!meta.has(c.carrierId)) {
        meta.set(c.carrierId, c.tradeName?.trim() ?? '')
      }
    }
  }
  return Array.from(meta.entries())
    .map(([carrierId, tradeName]) => ({ carrierId, tradeName }))
    .sort((a, b) => {
      const byName = a.tradeName.localeCompare(b.tradeName, undefined, { sensitivity: 'base' })
      if (byName !== 0) return byName
      return a.carrierId - b.carrierId
    })
}

function confirmedByCarrierId(carriers: DailyCarrierMetric[] | undefined): Map<number, number> {
  const m = new Map<number, number>()
  for (const c of carriers ?? []) {
    m.set(c.carrierId, c.confirmedOrders ?? 0)
  }
  return m
}

export interface DailyMetricsTableProps {
  data: DailyMetricsResponse | undefined
  isLoading: boolean
}

const DailyMetricsTable = ({ data, isLoading }: DailyMetricsTableProps) => {
  const { t } = useTranslation()
  const { selectedDay } = useDashboardFilters()
  const [ordersFillFilter, setOrdersFillFilter] = useState<DailyMetricFillFilter>('all')
  const [carriersFillFilter, setCarriersFillFilter] = useState<DailyMetricFillFilter>('all')

  const { columns, rows, summaryTotals } = useMemo(() => {
    const suppliers = data?.suppliers ?? []
    const allCarrierColumns = buildCarrierColumns(suppliers)

    const filteredSuppliers = suppliers.filter(s =>
      matchesFillFilter(s.orders ?? 0, ordersFillFilter),
    )

    const visibleCarrierColumns = allCarrierColumns.filter(({ carrierId }) => {
      const columnTotal = filteredSuppliers.reduce((sum, s) => {
        const byId = confirmedByCarrierId(s.carriers)
        return sum + (byId.get(carrierId) ?? 0)
      }, 0)
      return matchesFillFilter(columnTotal, carriersFillFilter)
    })

    const tableRows: TableRow[] = filteredSuppliers.map((s: DailySupplierMetric) => {
      const byId = confirmedByCarrierId(s.carriers)
      return {
        key: `supplier-${s.supplierId}`,
        supplierLabel: s.tradeName,
        orders: s.orders,
        breaches: s.breaches,
        carrierSlots: visibleCarrierColumns.map(({ carrierId }) => byId.get(carrierId) ?? 0),
      }
    })

    const dailySummaryTotals: DailySummaryTotals | null =
      tableRows.length > 0
        ? {
            supplierLabel: t('admin_router.dashboard.daily_table_total_row'),
            orders: tableRows.reduce((a, r) => a + r.orders, 0),
            breaches: tableRows.reduce((a, r) => a + r.breaches, 0),
            carrierSlots: visibleCarrierColumns.map((_, colIdx) =>
              tableRows.reduce((a, r) => a + (r.carrierSlots[colIdx] ?? 0), 0),
            ),
          }
        : null

    const cols: ColumnsType<TableRow> = [
      {
        title: t('admin_router.dashboard.daily_table_supplier'),
        dataIndex: 'supplierLabel',
        fixed: 'left',
        width: 200,
        render: (text: string) => <span>{text}</span>,
      },
      {
        title: t('admin_router.dashboard.daily_table_orders'),
        dataIndex: 'orders',
        fixed: 'left',
        align: 'center',
        width: 96,
        render: (value: number) => (
          <span
            style={{
              color: '#1677ff',
              fontWeight: 500,
            }}
          >
            {value === 0 ? '-' : value}
          </span>
        ),
      },
      {
        title: t('admin_router.dashboard.daily_table_breaches'),
        dataIndex: 'breaches',
        fixed: 'left',
        align: 'center',
        width: 110,
        onCell: (record: TableRow) => ({
          style: { ...breachHeatBackground(record.breaches, record.orders) },
        }),
        render: (value: number) => (
          <span
            style={{
              display: 'inline-block',
              minWidth: 32,
              padding: '2px 8px',
              borderRadius: 2,
            }}
          >
            {value === 0 ? '-' : value}
          </span>
        ),
      },
      ...visibleCarrierColumns.map(({ carrierId, tradeName }, colIdx) => {
        const headerLabel = tradeName || carrierId.toString()
        return {
          title: <TruncatedCarrierHeader label={headerLabel} />,
          align: 'center' as const,
          width: 60,
          render: (__: unknown, record: TableRow) => (
            <MetricsCenteredCell>
              {record.carrierSlots[colIdx] === 0 ? '-' : record.carrierSlots[colIdx] ?? 0}
            </MetricsCenteredCell>
          ),
        }
      }),
    ]

    return {
      columns: cols,
      rows: tableRows,
      summaryTotals: dailySummaryTotals,
    }
  }, [carriersFillFilter, data, ordersFillFilter, t])

  const dateLabel = dayjs(selectedDay).format('DD-MMM-YYYY')

  return (
    <>
      <DailyTableToolbar>
        <DailyTableToolbarFilters>
          <DailyMetricFillSelector
            label={t('admin_router.dashboard.metric_fill_orders')}
            value={ordersFillFilter}
            onChange={setOrdersFillFilter}
          />
          <DailyMetricFillSelector
            label={t('admin_router.dashboard.metric_fill_carriers')}
            value={carriersFillFilter}
            onChange={setCarriersFillFilter}
          />
        </DailyTableToolbarFilters>
        <DailyTableToolbarCenter>
          <DaySelector />
        </DailyTableToolbarCenter>
        <div />
      </DailyTableToolbar>
      <DailyTableDateBar>
        <span>
          {t('admin_router.dashboard.daily_date_label')}: {dateLabel}
        </span>
        <span>{t('admin_router.dashboard.daily_confirmed_orders_bar')}</span>
      </DailyTableDateBar>
      <DailyMetricsTableWrap>
        <Table<TableRow>
          loading={isLoading}
          className="daily-metrics-table"
          size="small"
          pagination={false}
          scroll={{ x: 'max-content', y: 43 * 10 }}
          columns={columns}
          dataSource={rows}
          locale={{ emptyText: t('admin_router.dashboard.daily_table_empty') }}
          summary={
            summaryTotals
              ? () => (
                  <Table.Summary fixed="bottom">
                    <Table.Summary.Row>
                      <Table.Summary.Cell
                        index={0}
                        align="left"
                        className="daily-summary-total-cell"
                      >
                        <span style={{ fontWeight: 600 }}>{summaryTotals.supplierLabel}</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={1}
                        align="center"
                        className="daily-summary-total-cell"
                      >
                        <span style={{ color: '#1677ff', fontWeight: 600 }}>
                          {summaryTotals.orders === 0 ? '-' : summaryTotals.orders}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={2}
                        align="center"
                        className="daily-summary-total-cell"
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: 32,
                            padding: '2px 8px',
                            borderRadius: 2,
                            fontWeight: 600,
                          }}
                        >
                          {summaryTotals.breaches === 0 ? '-' : summaryTotals.breaches}
                        </span>
                      </Table.Summary.Cell>
                      {summaryTotals.carrierSlots.map((value, colIdx) => (
                        <Table.Summary.Cell
                          key={colIdx}
                          index={3 + colIdx}
                          align="center"
                          className="daily-summary-total-cell"
                        >
                          <MetricsCenteredCell>
                            <span style={{ fontWeight: 600 }}>{value === 0 ? '-' : value}</span>
                          </MetricsCenteredCell>
                        </Table.Summary.Cell>
                      ))}
                    </Table.Summary.Row>
                  </Table.Summary>
                )
              : undefined
          }
        />
      </DailyMetricsTableWrap>
    </>
  )
}

export { DailyMetricsTable }
